import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PDFViewerState, PdfRenderTask } from '../../types';
import { usePdfDocumentQuery, usePdfRuntimeQuery } from '../../hooks/usePDFQuery';
import { PDFLoadingState } from '../shared/PDFLoadingState';
import { PDFErrorState } from '../shared/PDFErrorState';

/**
 * Render resolution multiplier — 2× for crisp Retina rendering.
 */
const RENDER_SCALE = 2;

interface PDFCanvasViewportProps {
  fileUrl: string;
  viewerState: PDFViewerState;
  onLoadSuccess: (numPages: number) => void;
  onScaleChange: (nextScale: number) => void;
  onPositionChange: (position: { x: number; y: number }) => void;
  /** Called once after the first page loads with the fit scale and centered position. */
  onFitComplete?: (fitScale: number, fitPosition: { x: number; y: number }) => void;
  /** Called when the PDF's intrinsic dimensions are known. */
  onDimensionsLoad?: (dimensions: { width: number; height: number }) => void;
  /**
   * Called when the user does a true click on the background (mousedown → mouseup with no drag).
   * Receives the clientX/Y of the click so callers can convert to content coordinates.
   */
  onBackgroundClick?: (clientX: number, clientY: number) => void;
  /** Text shown in the loading state. Defaults to "Loading Drawing...". */
  loadingMessage?: string;
  /**
   * Optional crop region in PDF points [x, y, width, height].
   * When set, only this region of the page is rendered and visible.
   * Dimensions reported to onDimensionsLoad will reflect the crop size.
   */
  cropRegion?: [number, number, number, number] | null;
}

export const PDFCanvasViewport: React.FC<PDFCanvasViewportProps> = ({
  fileUrl,
  viewerState,
  onLoadSuccess,
  onScaleChange,
  onPositionChange,
  onFitComplete,
  onDimensionsLoad,
  onBackgroundClick,
  loadingMessage = 'Loading Drawing...',
  cropRegion = null,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<PdfRenderTask | null>(null);

  const [pdfDimensions, setPdfDimensions] = useState<{ width: number; height: number } | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [isRendering, setIsRendering] = useState(false);

  // Drag state (refs — no re-renders needed)
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef(false);
  const [, forceRender] = useState(0); // only used to update cursor reactively

  const runtimeQuery = usePdfRuntimeQuery();
  const documentQuery = usePdfDocumentQuery(fileUrl);

  // Notify parent when page count is known.
  useEffect(() => {
    if (documentQuery.data) onLoadSuccess(documentQuery.data.numPages);
  }, [documentQuery.data, onLoadSuccess]);

  // Render PDF page to canvas whenever the document/page/rotation changes.
  useEffect(() => {
    const pdfDoc = documentQuery.data;
    const canvas = canvasRef.current;
    if (!pdfDoc || !canvas) return;

    let cancelled = false;

    const render = async () => {
      try {
        setRenderError(null);
        setIsRendering(true);

        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }

        const page = await pdfDoc.getPage(viewerState.currentPage);
        if (cancelled || !canvasRef.current) return;

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) throw new Error('Could not get canvas 2d context');

        // Natural (scale-1) dimensions — use crop region if set, otherwise full page.
        const naturalViewport = page.getViewport({ scale: 1, rotation: viewerState.rotation });
        const dimensions = cropRegion
          ? { width: cropRegion[2], height: cropRegion[3] }
          : { width: naturalViewport.width, height: naturalViewport.height };
        setPdfDimensions(dimensions);
        onDimensionsLoad?.(dimensions);

        // Render at 2× for crispness; display at 1× via CSS.
        const renderViewport = page.getViewport({ scale: RENDER_SCALE, rotation: viewerState.rotation });
        canvasRef.current.width = Math.round(dimensions.width * RENDER_SCALE);
        canvasRef.current.height = Math.round(dimensions.height * RENDER_SCALE);
        canvasRef.current.style.width = `${dimensions.width}px`;
        canvasRef.current.style.height = `${dimensions.height}px`;

        if (cropRegion) {
          // Shift the render context so the crop origin aligns with (0,0)
          ctx.translate(-cropRegion[0] * RENDER_SCALE, -cropRegion[1] * RENDER_SCALE);
        }

        const task = page.render({ canvasContext: ctx, viewport: renderViewport });
        renderTaskRef.current = task;
        await task.promise;

        if (!cancelled && renderTaskRef.current === task) {
          renderTaskRef.current = null;
          setIsRendering(false);
        }
      } catch (err) {
        const typed = err as { name?: string; message?: string };
        if (typed.name === 'RenderingCancelledException') return;
        if (!cancelled) {
          setRenderError(typed.message ?? 'Failed to render PDF page.');
          setIsRendering(false);
        }
      }
    };

    render();
    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [documentQuery.data, viewerState.currentPage, viewerState.rotation, cropRegion]);

  // ── Auto-fit: compute scale and centered position once per file load ──
  const hasFitRef = useRef(false);
  useEffect(() => {
    hasFitRef.current = false;
  }, [fileUrl, cropRegion]);

  useEffect(() => {
    if (!pdfDimensions || hasFitRef.current || !containerRef.current) return;

    const containerW = containerRef.current.clientWidth;
    const containerH = containerRef.current.clientHeight;
    if (!containerW || !containerH) return;

    const PADDING = 32; // px breathing room on each side
    const fitScale = Math.min(
      (containerW - PADDING * 2) / pdfDimensions.width,
      (containerH - PADDING * 2) / pdfDimensions.height,
    );

    // Center the scaled PDF within the container.
    const scaledW = pdfDimensions.width * fitScale;
    const scaledH = pdfDimensions.height * fitScale;
    const centeredX = (containerW - scaledW) / 2;
    const centeredY = (containerH - scaledH) / 2;

    hasFitRef.current = true;
    onFitComplete?.(fitScale, { x: centeredX, y: centeredY });
    onScaleChange(fitScale);
    onPositionChange({ x: centeredX, y: centeredY });
  }, [pdfDimensions, fileUrl, onFitComplete, onScaleChange, onPositionChange]);

  // ── Wheel zoom: zoom toward cursor, updating both scale and position ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      const zoomSpeed = 0.1;
      const factor = e.deltaY > 0 ? 1 - zoomSpeed : 1 + zoomSpeed;
      const newScale = Math.max(0.05, Math.min(20, viewerState.scale * factor));
      if (newScale === viewerState.scale) return;

      // Mouse position relative to the container element.
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Point under cursor in PDF coordinates (before zoom).
      const pdfX = (mouseX - viewerState.position.x) / viewerState.scale;
      const pdfY = (mouseY - viewerState.position.y) / viewerState.scale;

      // New position so that same PDF point stays under cursor after zoom.
      const newX = mouseX - pdfX * newScale;
      const newY = mouseY - pdfY * newScale;

      onPositionChange({ x: newX, y: newY });
      onScaleChange(newScale);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [viewerState.scale, viewerState.position, onScaleChange, onPositionChange]);

  // ── Drag to pan ──
  // Track mousedown position to distinguish a click from a drag.
  const mouseDownClientRef = useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    isDraggingRef.current = false;
    mouseDownClientRef.current = { x: e.clientX, y: e.clientY };
    dragStartRef.current = {
      x: e.clientX - viewerState.position.x,
      y: e.clientY - viewerState.position.y,
    };
    forceRender(n => n + 1);
  }, [viewerState.position]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;
      isDraggingRef.current = true;
      onPositionChange({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y,
      });
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!dragStartRef.current) return;
      const wasDrag = isDraggingRef.current;
      const downClient = mouseDownClientRef.current;
      dragStartRef.current = null;
      isDraggingRef.current = false;
      mouseDownClientRef.current = null;
      forceRender(n => n + 1);
      // Fire onBackgroundClick only for a true click (no movement).
      if (!wasDrag && downClient && onBackgroundClick) {
        onBackgroundClick(e.clientX, e.clientY);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [onPositionChange, onBackgroundClick]);

  // ── Touch: one-finger pan, two-finger pinch zoom ──
  const touchStartDistanceRef = useRef<number | null>(null);
  const touchStartScaleRef = useRef<number>(1);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let touchDragged = false;
    let touchStartClient: { x: number; y: number } | null = null;

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      touchDragged = false;
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        touchStartClient = { x: touch.clientX, y: touch.clientY };
        dragStartRef.current = {
          x: touch.clientX - viewerState.position.x,
          y: touch.clientY - viewerState.position.y,
        };
      } else if (e.touches.length === 2) {
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        touchStartDistanceRef.current = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        touchStartScaleRef.current = viewerState.scale;
        dragStartRef.current = null;
        touchStartClient = null;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      touchDragged = true;
      if (e.touches.length === 1 && dragStartRef.current) {
        const touch = e.touches[0];
        onPositionChange({
          x: touch.clientX - dragStartRef.current.x,
          y: touch.clientY - dragStartRef.current.y,
        });
      } else if (e.touches.length === 2 && touchStartDistanceRef.current !== null) {
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        const newScale = Math.max(0.05, Math.min(20, touchStartScaleRef.current * (dist / touchStartDistanceRef.current)));
        onScaleChange(newScale);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // Fire onBackgroundClick for a tap (no movement).
      if (!touchDragged && touchStartClient && e.changedTouches.length > 0 && onBackgroundClick) {
        const touch = e.changedTouches[0];
        onBackgroundClick(touch.clientX, touch.clientY);
      }
      touchStartDistanceRef.current = null;
      dragStartRef.current = null;
      touchStartClient = null;
      touchDragged = false;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [viewerState.scale, viewerState.position, onScaleChange, onPositionChange, onBackgroundClick]);

  const isLoading = runtimeQuery.isLoading || documentQuery.isLoading || isRendering;
  const errorMessage = runtimeQuery.isError
    ? runtimeQuery.error.message
    : documentQuery.isError
      ? documentQuery.error.message
      : renderError;

  const cursor = isDraggingRef.current ? 'grabbing' : 'grab';

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden bg-muted/50"
      style={{ cursor, touchAction: 'none', userSelect: 'none' }}
      onMouseDown={handleMouseDown}
    >
      {isLoading && (
        <div className="absolute inset-0 z-10">
          <PDFLoadingState message={loadingMessage} />
        </div>
      )}

      {errorMessage && (
        <div className="absolute inset-0 z-10">
          <PDFErrorState message={errorMessage} />
        </div>
      )}

      {/* PDF canvas — positioned and scaled via CSS transform */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          transformOrigin: '0 0',
          transform: `translate(${viewerState.position.x}px, ${viewerState.position.y}px) scale(${viewerState.scale})`,
          willChange: 'transform',
          boxShadow: pdfDimensions ? '0 4px 24px rgba(0,0,0,0.18)' : 'none',
          backgroundColor: 'white',
        }}
      >
        {/* Canvas is always in the DOM so the ref never goes missing */}
        <canvas ref={canvasRef} style={{ display: 'block' }} />
      </div>
    </div>
  );
};
